// The GraphQL operations behind "Мій сайт", the same ones the mobile app sends
// (apps/app/graphql/providerSite, apps/app/graphql/siteAssistant). Plain strings so tests can check
// them against the API schema without loading Apollo.

import type {
  ProviderSiteJobStatus,
  ProviderSiteMessageRole,
  ProviderSiteStatus,
  SiteLanguage,
} from "./provider-site";

export interface ProviderSite {
  uuid: string;
  profileUuid: string;
  slug: string;
  url: string;
  lang: SiteLanguage;
  status: ProviderSiteStatus;
  publishedVersion: number | null;
  hasDraft: boolean;
  draftPreviewUrl: string | null;
  hasUnpublishedChanges: boolean;
  readyDesign: string | null;
  remainingBuilds: number | null;
  remainingEdits: number | null;
  limitsResetAt: string | null;
  activeJob: {
    uuid: string;
    kind: "BUILD" | "EDIT";
    status: ProviderSiteJobStatus;
    step: string | null;
  } | null;
  messages: { uuid: string; role: ProviderSiteMessageRole; text: string; createdAt: string }[];
  versions: {
    number: number;
    summary: string | null;
    readyDesign: string | null;
    createdAt: string;
  }[];
}

export interface ReadyDesign {
  id: string;
  name: string;
  group: string;
  tags: string[];
  thumbUrl: string;
  // Null for a design published before desktop thumbnails.
  thumbDesktopUrl: string | null;
}

export interface ReadyDesigns {
  groups: string[];
  designs: ReadyDesign[];
}

export interface SiteAssistantRequest {
  uuid: string;
  clientName: string | null;
  status: "CREATED" | "PENDING" | "ALLOWED" | "DENIED" | "USED";
  createdAt: string;
  expiresAt: string;
}

export interface SiteAssistantConnection {
  uuid: string;
  clientName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface SiteAssistant {
  pendingRequest: SiteAssistantRequest | null;
  connections: SiteAssistantConnection[];
}

const PROVIDER_SITE_FIELDS = `
  fragment ProviderSiteFields on ProviderSite {
    uuid
    profileUuid
    slug
    url
    lang
    status
    publishedVersion
    hasDraft
    draftPreviewUrl
    hasUnpublishedChanges
    readyDesign
    remainingBuilds
    remainingEdits
    limitsResetAt
    activeJob {
      uuid
      kind
      status
      step
    }
    messages {
      uuid
      role
      text
      createdAt
    }
    versions {
      number
      summary
      readyDesign
      createdAt
    }
  }
`;

export const MY_PROVIDER_SITE_QUERY = `
  query MyProviderSite($profileUuid: ID!) {
    myProviderSite(profileUuid: $profileUuid) {
      ...ProviderSiteFields
    }
  }
  ${PROVIDER_SITE_FIELDS}
`;

// The Design library for the Provider: their own groups first; thumbnail links expire after an
// hour, so every visit reads the list again.
export const READY_DESIGNS_QUERY = `
  query ReadyDesigns($profileUuid: ID!) {
    readyDesigns(profileUuid: $profileUuid) {
      groups
      designs {
        id
        name
        group
        tags
        thumbUrl
        thumbDesktopUrl
      }
    }
  }
`;

// "Мій сайт" needs the `provider_site` and `ai_designer` Entitlements. The selection matches
// subscription/subscription.api.ts, so both share one cached `mySubscription`.
export const SITE_ENTITLEMENTS_QUERY = `
  query SiteEntitlements {
    mySubscription {
      plan {
        key
        name
        rank
      }
      subscription {
        status
        cancelAtPeriodEnd
        currentPeriodEndsAt
      }
      entitlements {
        key
        booleanValue
        integerValue
        source
      }
    }
  }
`;

// Every site mutation answers with the whole site, so Apollo updates the pages from its cache.
function siteMutation(name: string, extraArgs = "", extraParams = "") {
  return `
    mutation ${name[0].toUpperCase() + name.slice(1)}($profileUuid: ID!${extraParams}) {
      ${name}(profileUuid: $profileUuid${extraArgs}) {
        ...ProviderSiteFields
      }
    }
    ${PROVIDER_SITE_FIELDS}
  `;
}

export const SEND_MESSAGE_MUTATION = siteMutation(
  "providerSiteSendMessage",
  ", text: $text",
  ", $text: String!"
);
export const PUBLISH_MUTATION = siteMutation("providerSitePublish");
export const RESTORE_VERSION_MUTATION = siteMutation(
  "providerSiteRestoreVersion",
  ", number: $number",
  ", $number: Int!"
);
export const APPLY_READY_DESIGN_MUTATION = siteMutation(
  "providerSiteApplyReadyDesign",
  ", id: $id",
  ", $id: ID!"
);
export const DISCARD_DRAFT_MUTATION = siteMutation("providerSiteDiscardDraft");
export const DISABLE_MUTATION = siteMutation("providerSiteDisable");
export const ENABLE_MUTATION = siteMutation("providerSiteEnable");
export const SET_LANGUAGE_MUTATION = siteMutation(
  "providerSiteSetLanguage",
  ", lang: $lang",
  ", $lang: String!"
);

// Renders a design on the Provider's own data without touching the draft; answers the preview URL.
export const TRY_READY_DESIGN_MUTATION = `
  mutation ProviderSiteTryReadyDesign($profileUuid: ID!, $id: ID!) {
    providerSiteTryReadyDesign(profileUuid: $profileUuid, id: $id) {
      id
      previewUrl
    }
  }
`;

export const MY_SITE_ASSISTANT_QUERY = `
  query MySiteAssistant {
    mySiteAssistant {
      pendingRequest {
        uuid
        clientName
        status
        createdAt
        expiresAt
      }
      connections {
        uuid
        clientName
        createdAt
        lastUsedAt
      }
    }
  }
`;

export const SITE_ASSISTANT_CREATE_PAIRING_MUTATION = `
  mutation SiteAssistantCreatePairing {
    siteAssistantCreatePairing {
      installUrl
      expiresAt
    }
  }
`;

export const SITE_ASSISTANT_ANSWER_MUTATION = `
  mutation SiteAssistantAnswer($requestUuid: ID!, $allow: Boolean!) {
    siteAssistantAnswer(requestUuid: $requestUuid, allow: $allow)
  }
`;

export const SITE_ASSISTANT_DISCONNECT_MUTATION = `
  mutation SiteAssistantDisconnect {
    siteAssistantDisconnect
  }
`;
